import { useState, useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface AnimatedTab {
  label: string;
  content: React.ReactNode;
}

interface AnimatedTabsProps {
  tabs: AnimatedTab[];
  className?: string;
}

const AnimatedTabs = ({ tabs, className }: AnimatedTabsProps) => {
  const [active, setActive] = useState(0);
  const [indicatorStyle, setIndicatorStyle] = useState<React.CSSProperties>({});
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const el = tabRefs.current[active];
    if (el) {
      setIndicatorStyle({
        left: el.offsetLeft,
        width: el.offsetWidth,
      });
    }
  }, [active]);

  return (
    <div className={cn("w-full", className)}>
      {/* Tab bar */}
      <div
        className="relative flex border-b border-border mb-8"
        role="tablist"
        aria-label="Content tabs"
      >
        {tabs.map((tab, i) => (
          <button
            key={i}
            ref={(el) => { tabRefs.current[i] = el; }}
            role="tab"
            aria-selected={active === i}
            aria-controls={`tabpanel-${i}`}
            id={`tab-${i}`}
            onClick={() => setActive(i)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight") {
                e.preventDefault();
                setActive((active + 1) % tabs.length);
              } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                setActive((active - 1 + tabs.length) % tabs.length);
              }
            }}
            tabIndex={active === i ? 0 : -1}
            className={cn(
              "font-body text-xs uppercase tracking-widest px-5 py-3 transition-colors duration-300",
              active === i
                ? "text-accent"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {tab.label}
          </button>
        ))}
        {/* Sliding indicator */}
        <div
          className="absolute bottom-0 h-0.5 bg-accent transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={indicatorStyle}
        />
      </div>

      {/* Tab panels with crossfade */}
      <div className="relative">
        {tabs.map((tab, i) => (
          <div
            key={i}
            role="tabpanel"
            id={`tabpanel-${i}`}
            aria-labelledby={`tab-${i}`}
            hidden={active !== i}
            className={cn(
              "transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)]",
              active === i
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-2"
            )}
          >
            {tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnimatedTabs;
